/**
 * Operators of an ERP filter, and which of them a field kind may use.
 * The names match the engine allowlist (`starts` / `ends`, not the underscore
 * forms). Dashboard and the sales search both read this table.
 */

export type FilterOp =
  | 'eq'
  | 'neq'
  | 'in'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'contains'
  | 'starts'
  | 'ends';

/** Operators offered in a filter builder (order = UI order). */
export const FILTER_OPS: readonly FilterOp[] = [
  'eq',
  'neq',
  'contains',
  'starts',
  'ends',
  'gt',
  'gte',
  'lt',
  'lte',
  'in',
] as const;

export type FilterFieldKind =
  | 'text'
  | 'number'
  | 'decimal'
  | 'date'
  | 'timestamp'
  | 'boolean'
  | 'relation';

const NUMERIC_OPS: readonly FilterOp[] = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in'];
const DATE_OPS: readonly FilterOp[] = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'];
// Range ops stay meaningful with date-only literals against timestamptz.
// eq/neq/in compare against midnight and silently match nothing.
const TIMESTAMP_OPS: readonly FilterOp[] = ['gt', 'gte', 'lt', 'lte'];
const EQ_OPS: readonly FilterOp[] = ['eq', 'neq'];
// Relation also offers `in` (multi-pick). The comma-id text widget remains
// the path for a target whose picker cannot work.
const RELATION_OPS: readonly FilterOp[] = ['eq', 'neq', 'in'];

const OPS_BY_KIND: Record<FilterFieldKind, readonly FilterOp[]> = {
  text: FILTER_OPS,
  number: NUMERIC_OPS,
  decimal: NUMERIC_OPS,
  date: DATE_OPS,
  timestamp: TIMESTAMP_OPS,
  boolean: EQ_OPS,
  relation: RELATION_OPS,
};

export function isFilterOp(v: string): v is FilterOp {
  return (FILTER_OPS as readonly string[]).includes(v);
}

/** Ops for a kind. An unknown kind gets the full list (free-typed field). */
export function opsForKind(kind: FilterFieldKind | undefined): readonly FilterOp[] {
  return kind ? OPS_BY_KIND[kind] : FILTER_OPS;
}
